/**
 * StorageService
 * Camada centralizada de abstração para acesso seguro ao localStorage.
 * Trata exceções de cota excedida, JSON inválido, ausência de storage, etc.
 */

export type StorageKeyCategory = "PERMANENT" | "TEMPORARY" | "VOLATILE";

export const StorageCategory = {
  PERMANENT: "PERMANENT" as StorageKeyCategory,
  TEMPORARY: "TEMPORARY" as StorageKeyCategory,
  VOLATILE: "VOLATILE" as StorageKeyCategory
};

interface TTLMetadata<T> {
  __isTTL: true;
  category: StorageKeyCategory;
  createdAt: number;
  expiresAt: number;
  value: T;
}

class StorageServiceImpl {
  private memoryStore: Map<string, string> = new Map();

  // Chaves conhecidas permanentes que NUNCA devem ser limpas automaticamente
  public static readonly PERMANENT_KEYS = [
    "app_user_profile",
    "app_user_session",
    "atletahub_user",
    "theme",
    "user_preferences"
  ];

  // Chaves temporárias que possuem TTL padrão
  public static readonly DEFAULT_TTL_MS = 24 * 60 * 60 * 1000; // 24 horas

  private getStorage(): Storage | null {
    if (typeof window !== "undefined" && window.localStorage) {
      return window.localStorage;
    }
    if (typeof globalThis !== "undefined" && (globalThis as any).localStorage) {
      return (globalThis as any).localStorage;
    }
    return null;
  }

  /**
   * Salva um item com tempo de expiração (TTL) e categoria.
   */
  public setWithTTL<T>(
    key: string,
    value: T,
    ttlMs: number = StorageServiceImpl.DEFAULT_TTL_MS,
    category: StorageKeyCategory = "TEMPORARY"
  ): boolean {
    const now = Date.now();
    const payload: TTLMetadata<T> = {
      __isTTL: true,
      category,
      createdAt: now,
      expiresAt: now + ttlMs,
      value
    };
    return this.set<TTLMetadata<T>>(key, payload);
  }

  /**
   * Recupera um item com validação de expiração TTL.
   * Se o item estiver expirado, remove automaticamente do storage.
   */
  public getWithTTL<T>(key: string, fallbackValue: T | null = null): T | null {
    const rawData = this.get<any>(key, null);
    if (!rawData) return fallbackValue;

    // Se é um envelope TTL
    if (rawData && typeof rawData === "object" && rawData.__isTTL === true) {
      const now = Date.now();
      if (now > rawData.expiresAt) {
        this.remove(key);
        return fallbackValue;
      }
      return rawData.value as T;
    }

    // Se não tinha envelope TTL, retorna o valor direto
    return rawData as T;
  }

  /**
   * Executa varredura de todas as chaves e remove itens com TTL vencido.
   */
  public cleanupExpired(): number {
    let removedCount = 0;
    const now = Date.now();
    const removedKeys = new Set<string>();

    try {
      const storage = this.getStorage();
      if (storage) {
        const keys: string[] = [];
        for (let i = 0; i < storage.length; i++) {
          const k = storage.key(i);
          if (k) keys.push(k);
        }

        for (const k of keys) {
          // Ignora chaves do Firebase Auth ou permanentes protegidas
          if (k.startsWith("firebase:") || StorageServiceImpl.PERMANENT_KEYS.includes(k)) {
            continue;
          }

          try {
            const raw = storage.getItem(k);
            if (!raw) continue;
            if (raw.includes('"__isTTL":true') || raw.includes('"expiresAt":')) {
              const parsed = JSON.parse(raw);
              if (parsed && parsed.__isTTL && parsed.expiresAt && now > parsed.expiresAt) {
                storage.removeItem(k);
                this.memoryStore.delete(k);
                removedKeys.add(k);
                removedCount++;
              }
            }
          } catch {}
        }
      }

      // Varredura de fallback para memoryStore (para SSR, testes e ambientes sem window)
      for (const [k, raw] of this.memoryStore.entries()) {
        if (removedKeys.has(k)) continue;
        if (k.startsWith("firebase:") || StorageServiceImpl.PERMANENT_KEYS.includes(k)) {
          continue;
        }

        try {
          if (raw.includes('"__isTTL":true') || raw.includes('"expiresAt":')) {
            const parsed = JSON.parse(raw);
            if (parsed && parsed.__isTTL && parsed.expiresAt && now > parsed.expiresAt) {
              this.memoryStore.delete(k);
              if (storage) storage.removeItem(k);
              removedCount++;
            }
          }
        } catch {}
      }
    } catch (err) {
      console.warn("[StorageService] Erro durante rotina de limpeza de expirados:", err);
    }
    return removedCount;
  }

  /**
   * Verifica se o localStorage está disponível e acessível no ambiente atual.
   */
  public isAvailable(): boolean {
    try {
      const storage = this.getStorage();
      if (!storage) return false;
      const testKey = "__storage_test__";
      storage.setItem(testKey, "1");
      storage.removeItem(testKey);
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Obtém e faz parse de um item armazenado no localStorage.
   */
  public get<T>(key: string, fallbackValue: T | null = null): T | null {
    if (!this.isAvailable()) {
      const mem = this.memoryStore.get(key);
      if (mem !== undefined) {
        try {
          return JSON.parse(mem) as T;
        } catch {
          return fallbackValue;
        }
      }
      return fallbackValue;
    }
    try {
      const storage = this.getStorage();
      const raw = storage ? storage.getItem(key) : null;
      if (raw === null || raw === undefined) {
        return fallbackValue;
      }
      return JSON.parse(raw) as T;
    } catch (error) {
      console.warn(`[StorageService] Erro ao deserializar chave "${key}":`, error);
      return fallbackValue;
    }
  }

  /**
   * Obtém a string pura armazenada no localStorage sem processamento JSON.
   */
  public getRaw(key: string, fallbackValue: string | null = null): string | null {
    if (!this.isAvailable()) {
      return this.memoryStore.get(key) ?? fallbackValue;
    }
    try {
      const storage = this.getStorage();
      const raw = storage ? storage.getItem(key) : null;
      return raw !== null ? raw : fallbackValue;
    } catch (error) {
      console.warn(`[StorageService] Erro ao ler raw key "${key}":`, error);
      return fallbackValue;
    }
  }

  /**
   * Serializa e salva um item no localStorage.
   */
  public set<T>(key: string, value: T): boolean {
    const serialized = JSON.stringify(value);
    this.memoryStore.set(key, serialized);
    if (!this.isAvailable()) return true;
    try {
      const storage = this.getStorage();
      if (storage) {
        storage.setItem(key, serialized);
      }
      return true;
    } catch (error) {
      if (error instanceof DOMException && (error.name === "QuotaExceededError" || error.code === 22)) {
        console.error(`[StorageService] Cota de localStorage excedida ao salvar chave "${key}".`);
      } else {
        console.error(`[StorageService] Erro ao salvar chave "${key}":`, error);
      }
      return false;
    }
  }

  /**
   * Salva uma string pura no localStorage.
   */
  public setRaw(key: string, value: string): boolean {
    this.memoryStore.set(key, value);
    if (!this.isAvailable()) return true;
    try {
      const storage = this.getStorage();
      if (storage) {
        storage.setItem(key, value);
      }
      return true;
    } catch (error) {
      console.error(`[StorageService] Erro ao salvar raw key "${key}":`, error);
      return false;
    }
  }

  /**
   * Remove uma chave do localStorage.
   */
  public remove(key: string): boolean {
    this.memoryStore.delete(key);
    if (!this.isAvailable()) return true;
    try {
      const storage = this.getStorage();
      if (storage) {
        storage.removeItem(key);
      }
      return true;
    } catch (error) {
      console.error(`[StorageService] Erro ao remover chave "${key}":`, error);
      return false;
    }
  }

  /**
   * Verifica se uma chave existe no localStorage.
   */
  public has(key: string): boolean {
    if (!this.isAvailable()) return this.memoryStore.has(key);
    try {
      const storage = this.getStorage();
      return storage ? storage.getItem(key) !== null : false;
    } catch {
      return false;
    }
  }

  /**
   * Retorna todas as chaves no localStorage que iniciam com um determinado prefixo.
   */
  public getKeysWithPrefix(prefix: string): string[] {
    if (!this.isAvailable()) {
      return Array.from(this.memoryStore.keys()).filter((k) => k.startsWith(prefix));
    }
    const matchedKeys: string[] = [];
    try {
      const storage = this.getStorage();
      if (storage) {
        const len = storage.length;
        for (let i = 0; i < len; i++) {
          const k = storage.key(i);
          if (k && k.startsWith(prefix)) {
            matchedKeys.push(k);
          }
        }
      }
    } catch (error) {
      console.error(`[StorageService] Erro ao buscar chaves com prefixo "${prefix}":`, error);
    }
    return matchedKeys;
  }

  /**
   * Remove todas as chaves que iniciam com um determinado prefixo.
   */
  public clearByPrefix(prefix: string): void {
    const keys = this.getKeysWithPrefix(prefix);
    keys.forEach((k) => this.remove(k));
  }

  /**
   * Helper exclusivo para testes unitários
   */
  public resetForTest(): void {
    this.memoryStore.clear();
    const storage = this.getStorage();
    if (storage && typeof storage.clear === "function") {
      storage.clear();
    }
  }
}

export const StorageService = new StorageServiceImpl();
